import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiScissors, FiUser, FiStar } = FiIcons;

function Services() {
  const services = [
    {
      icon: FiScissors,
      name: 'Classic Haircut',
      description: 'Traditional barbering techniques for timeless styles',
      price: 'From $35',
      features: ['Wash & Cut', 'Styling', 'Hot Towel']
    },
    {
      icon: FiUser,
      name: 'Beard Trim',
      description: 'Professional beard shaping and maintenance',
      price: 'From $25',
      features: ['Trim & Shape', 'Hot Towel', 'Beard Oil']
    },
    {
      icon: FiStar,
      name: 'Premium Package',
      description: 'Complete grooming experience with all services',
      price: 'From $65',
      features: ['Cut & Style', 'Beard Trim', 'Hot Towel', 'Massage']
    },
    {
      icon: FiScissors,
      name: 'Fade Cuts',
      description: 'Modern fade styles and contemporary cuts',
      price: 'From $40',
      features: ['Precision Fade', 'Styling', 'Finish Touch']
    },
    {
      icon: FiUser,
      name: 'Shampoo & Style',
      description: 'Deep cleanse and professional styling',
      price: 'From $30',
      features: ['Deep Shampoo', 'Conditioning', 'Styling']
    },
    {
      icon: FiStar,
      name: 'Hot Towel Shave',
      description: 'Traditional straight razor shave experience',
      price: 'From $45',
      features: ['Straight Razor', 'Hot Towel', 'Aftercare']
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <section className="bg-red-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            Our Services
          </motion.h1>
          <motion.p
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="text-lg md:text-xl max-w-3xl mx-auto"
          >
            Professional barber services tailored to your style and preferences. We offer a complete range of grooming services for the modern gentleman.
          </motion.p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1, duration: 0.8 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden border border-gray-100"
              >
                <div className="p-8">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                    <SafeIcon icon={service.icon} className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {service.name}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {service.description}
                  </p>
                  <div className="text-2xl font-bold text-red-600 mb-6">
                    {service.price}
                  </div>
                  <ul className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-gray-50 px-8 py-4">
                  <a
                    href="tel:+14165327560"
                    className="block w-full text-center bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-semibold transition-colors duration-200"
                  >
                    Book Now
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Professional Excellence
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              All our services include consultation to ensure you get the perfect cut for your face shape, lifestyle, and personal style. We use only premium products and maintain the highest standards of cleanliness and professionalism.
            </p>
            <div className="bg-red-600 text-white p-6 rounded-lg max-w-2xl mx-auto">
              <h3 className="text-xl font-bold mb-2">Walk-ins Welcome</h3>
              <p className="mb-4">
                No appointment necessary, but calling ahead is recommended during peak hours.
              </p>
              <a
                href="tel:+14165327560"
                className="bg-white text-red-600 hover:bg-gray-100 px-6 py-2 rounded-lg font-semibold transition-colors duration-200 inline-block"
              >
                Call +1 (416) 532-7560
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
}

export default Services;